import React from 'react';
import { Mail, Send, Shield, Clock, Globe } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { siteContent, contactInfo } from '../data';

const Contact = () => {
  return (
    <div className="min-h-screen bg-black">
      {/* Hero Section */}
      <section className="pt-32 pb-16 relative">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-cyan-900/20 via-black to-black"></div>
        <div className="absolute inset-0">
          <div className="absolute top-20 right-20 w-72 h-72 bg-cyan-500/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-20 w-72 h-72 bg-green-500/10 rounded-full blur-3xl"></div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <div className="inline-flex items-center px-4 py-2 bg-cyan-500/10 border border-cyan-500/30 rounded-full mb-6 backdrop-blur-sm">
              <Shield className="w-4 h-4 text-cyan-400 mr-2" />
              <span className="text-cyan-400 text-sm font-medium">24/7 Security Support</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">{siteContent.contact.title}</h1>
            <p className="text-xl text-gray-400">{siteContent.contact.description}</p>
          </div>
        </div>
      </section>

      {/* Contact Cards Section */}
      <section className="pb-20 relative">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* Telegram Card */}
            <Card 
              className="bg-gray-900/30 border-cyan-900/30 backdrop-blur-sm hover:bg-gray-900/50 transition-all duration-300 cursor-pointer group hover:border-cyan-500/50 hover:shadow-lg hover:shadow-cyan-500/10"
              onClick={() => window.open(contactInfo.telegram, '_blank')}
            >
              <CardHeader>
                <div className="w-20 h-20 bg-gradient-to-br from-cyan-500/20 to-green-500/20 rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                  <Send className="w-10 h-10 text-cyan-400" />
                </div>
                <CardTitle className="text-white text-2xl">{siteContent.contact.telegram}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-400 mb-4">Get instant support via Telegram. Our team typically responds within minutes.</p>
                <div className="flex items-center justify-between">
                  <p className="text-cyan-400 text-lg font-mono">{contactInfo.telegramHandle}</p>
                  <span className="text-xs text-green-400 bg-green-500/10 px-2 py-1 rounded-full">Online</span>
                </div>
              </CardContent>
            </Card>

            {/* Email Card */}
            <Card 
              className="bg-gray-900/30 border-cyan-900/30 backdrop-blur-sm hover:bg-gray-900/50 transition-all duration-300 cursor-pointer group hover:border-cyan-500/50 hover:shadow-lg hover:shadow-cyan-500/10"
              onClick={() => window.location.href = contactInfo.email}
            >
              <CardHeader>
                <div className="w-20 h-20 bg-gradient-to-br from-cyan-500/20 to-green-500/20 rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                  <Mail className="w-10 h-10 text-cyan-400" />
                </div>
                <CardTitle className="text-white text-2xl">{siteContent.contact.email}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-400 mb-4">Email us for detailed inquiries, partnerships, or enterprise solutions.</p>
                <p className="text-cyan-400 text-lg font-mono">{contactInfo.emailDisplay}</p>
              </CardContent>
            </Card>
          </div>

          {/* Info Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto mt-12">
            {[
              { icon: Clock, title: '24/7 Availability', desc: 'Round-the-clock monitoring and support for all clients' },
              { icon: Globe, title: 'Global Coverage', desc: 'Enforcement network spanning across continents' },
              { icon: Shield, title: 'Immediate Response', desc: 'Average threat response time under 15 minutes' }
            ].map((item, idx) => (
              <div key={idx} className="text-center p-6 rounded-2xl bg-gray-900/20 border border-cyan-900/20">
                <item.icon className="w-8 h-8 text-cyan-400 mx-auto mb-3" />
                <h3 className="text-white font-semibold mb-2">{item.title}</h3>
                <p className="text-gray-400 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gradient-to-b from-black via-gray-950 to-black">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-white mb-4">Frequently Asked Questions</h2>
              <p className="text-gray-400">Quick answers to common questions</p>
            </div>

            <div className="space-y-4">
              {[
                { q: 'How quickly can you respond to a threat?', a: 'Our average response time is under 15 minutes. Critical threats are escalated immediately to our senior security team.' },
                { q: 'Do you offer custom enterprise solutions?', a: 'Yes, we provide tailored security solutions for enterprises. Contact us to discuss your specific requirements.' },
                { q: 'What is your global coverage?', a: 'We have enforcement partnerships in over 150 countries, allowing us to take action against threats worldwide.' },
                { q: 'How do you handle DMCA takedowns?', a: 'We handle the entire process from detection to removal, including drafting legal notices and coordinating with hosting providers.' }
              ].map((faq, idx) => (
                <div key={idx} className="bg-gray-900/30 border border-cyan-900/30 rounded-xl p-6 hover:border-cyan-500/30 transition-colors duration-300">
                  <h3 className="text-white font-semibold mb-2">{faq.q}</h3>
                  <p className="text-gray-400 text-sm">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;